import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesDirectGasEmissionsListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsActivitiesDirectGasEmissionsCreate,
  useCompanyReportsActivitiesDirectGasEmissionsList,
  useCompanyReportsActivitiesDirectGasEmissionsPreviewRetrieve,
  useCompanyReportsActivitiesDirectGasEmissionsRetrieve,
  useCompanyReportsActivitiesDirectGasEmissionsUpdate,
} from '@shared/api/lib/company-reports/company-reports';
import {
  CompanyReportsActivitiesDirectGasEmissionsListParams,
  CompanyReportsActivitiesDirectGasEmissionsPreviewRetrieveParams,
  DirectGasEmissionActivitiesList,
  DirectGasEmissionActivityRetrieve,
  PaginatedDirectGasEmissionActivitiesListList,
} from '@shared/api/types';

import { useQueryClient } from '@tanstack/react-query';

export const useGetDirectGasEmissionsActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesDirectGasEmissionsListParams
) => {
  return useCompanyReportsActivitiesDirectGasEmissionsList<PaginatedDirectGasEmissionActivitiesListList>(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 24,
      },
    }
  );
};

export const useCreateDirectGasEmissionsActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDirectGasEmissionsCreate<DirectGasEmissionActivitiesList>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesDirectGasEmissionsListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useGetDirectGasEmissionsActivity = ({
  directGasActivityUuid,
  reportUuid,
}: {
  directGasActivityUuid?: string;
  reportUuid: string;
}) => {
  return useCompanyReportsActivitiesDirectGasEmissionsRetrieve<DirectGasEmissionActivityRetrieve>(
    reportUuid,
    directGasActivityUuid!,
    {
      request: useAxiosConfig(),
      query: {
        enabled: !!directGasActivityUuid,
      },
    }
  );
};

export const useUpdateDirectGasEmissionsActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDirectGasEmissionsUpdate<DirectGasEmissionActivitiesList>(
    {
      request: useAxiosConfig(),
      mutation: {
        onSuccess: (data, variables) => {
          void refreshQueries(
            queryClient,
            getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
          );
          return refreshQueries(
            queryClient,
            getCompanyReportsActivitiesDirectGasEmissionsListQueryKey(
              variables.companyReportUuid
            )
          );
        },
      },
    }
  );
};

export const useDeleteDirectGasEmissionsActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesDirectGasEmissionsListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useGetDirectGasEmissionsPreview = ({
  companyReportUuid,
  params,
  enabled = true,
}: {
  companyReportUuid: string;
  params?: CompanyReportsActivitiesDirectGasEmissionsPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return useCompanyReportsActivitiesDirectGasEmissionsPreviewRetrieve(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        enabled: enabled,
        cacheTime: 0,
        staleTime: 0,
      },
    }
  );
};
